import React from 'react';

/**
 * Small Markdown renderer for authored documentation pages.
 *
 * Output is built as React elements rather than an HTML string, so authored
 * content can never inject markup into the docs pages — including the public
 * ones. Links are restricted to http(s)/mailto/relative for the same reason.
 *
 * Supports: headings, paragraphs, fenced code, bullet/ordered lists, tables,
 * blockquotes, rules, and inline code/bold/italic/links.
 *
 * variant="app" (default) styles from theme tokens and follows dark/light.
 * variant="light" is a fixed light palette for the standalone public docs.
 */
export default function Markdown({ content, variant = 'app', style }) {
  const c = variant === 'light' ? LIGHT : APP;
  return <div style={{ ...style }}>{renderBlocks(String(content || ''), c)}</div>;
}

// In-app palette: pure tokens, so it follows the dark/light theme.
const APP = {
  text: 'var(--text-1)',
  muted: 'var(--text-2)',
  heading: 'var(--text-1)',
  link: 'var(--accent)',
  codeBg: 'var(--code-bg)',
  codeText: 'var(--code-text)',
  inlineBg: 'var(--inline-bg)',
  inlineText: 'var(--inline-text)',
  border: 'var(--border)',
  quoteBar: 'var(--accent)',
  tableHeadBg: 'var(--bg-elevated)',
};

const LIGHT = {
  text: '#374151',
  muted: '#6b7280',
  heading: '#111827',
  link: '#5865f2',
  codeBg: '#0f172a',
  codeText: '#e2e8f0',
  inlineBg: '#eef2ff',
  inlineText: '#4338ca',
  border: '#e5e7eb',
  quoteBar: '#5865f2',
  tableHeadBg: '#f9fafb',
};

const SAFE_LINK = /^(https?:\/\/|mailto:|\/|#)/i;

function renderBlocks(src, c) {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code
    const fence = line.match(/^```\s*(\S+)?\s*$/);
    if (fence) {
      const lang = fence[1] || '';
      const body = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1; // closing fence
      out.push(
        <pre key={key++} style={{
          background: c.codeBg, color: c.codeText, borderRadius: 8, padding: '12px 14px',
          overflowX: 'auto', fontSize: 12.5, lineHeight: 1.6, margin: '0 0 16px',
          fontFamily: 'var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
          border: `1px solid ${c.border}`,
        }}>
          {lang && <div style={{ color: c.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{lang}</div>}
          <code>{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const sizes = { 1: 26, 2: 20, 3: 16, 4: 14 };
      const Tag = `h${level}`;
      out.push(
        <Tag key={key++} style={{
          fontSize: sizes[level], fontWeight: level < 3 ? 800 : 700, color: c.heading,
          margin: out.length ? '24px 0 10px' : '0 0 10px', lineHeight: 1.3,
        }}>
          {renderInline(h[2], c)}
        </Tag>,
      );
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^(---+|\*\*\*+|___+)\s*$/.test(line)) {
      out.push(<hr key={key++} style={{ border: 0, borderTop: `1px solid ${c.border}`, margin: '20px 0' }} />);
      i += 1;
      continue;
    }

    // Table — a header row followed by a |---|---| separator
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const cells = (row) => row.trim().replace(/^\||\|$/g, '').split('|').map(s => s.trim());
      const head = cells(line);
      i += 2;
      const body = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { body.push(cells(lines[i])); i += 1; }
      out.push(
        <div key={key++} style={{ overflowX: 'auto', margin: '0 0 16px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>{head.map((cell, n) => (
                <th key={n} style={{ textAlign: 'left', padding: '8px 10px', background: c.tableHeadBg, color: c.heading, border: `1px solid ${c.border}`, fontWeight: 700 }}>
                  {renderInline(cell, c)}
                </th>
              ))}</tr>
            </thead>
            <tbody>{body.map((row, r) => (
              <tr key={r}>{row.map((cell, n) => (
                <td key={n} style={{ padding: '8px 10px', border: `1px solid ${c.border}`, color: c.text, verticalAlign: 'top' }}>
                  {renderInline(cell, c)}
                </td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const body = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { body.push(lines[i].replace(/^>\s?/, '')); i += 1; }
      out.push(
        <blockquote key={key++} style={{
          borderLeft: `3px solid ${c.quoteBar}`, padding: '4px 0 4px 14px', margin: '0 0 16px',
          color: c.muted, fontSize: 14, lineHeight: 1.7,
        }}>
          {renderInline(body.join(' '), c)}
        </blockquote>,
      );
      continue;
    }

    // Lists
    const isBullet = (s) => /^\s*[-*+]\s+/.test(s);
    const isNumber = (s) => /^\s*\d+[.)]\s+/.test(s);
    if (isBullet(line) || isNumber(line)) {
      const ordered = isNumber(line);
      const items = [];
      while (i < lines.length && (ordered ? isNumber(lines[i]) : isBullet(lines[i]))) {
        items.push(lines[i].replace(ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*+]\s+/, ''));
        i += 1;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      out.push(
        <ListTag key={key++} style={{ margin: '0 0 16px', paddingLeft: 22, color: c.text, fontSize: 14, lineHeight: 1.8 }}>
          {items.map((item, n) => <li key={n}>{renderInline(item, c)}</li>)}
        </ListTag>,
      );
      continue;
    }

    // Blank line
    if (!line.trim()) { i += 1; continue; }

    // Paragraph — consume until a blank line or the start of another block
    const para = [];
    while (
      i < lines.length && lines[i].trim()
      && !/^```/.test(lines[i]) && !/^#{1,4}\s/.test(lines[i]) && !/^>\s?/.test(lines[i])
      && !isBullet(lines[i]) && !isNumber(lines[i]) && !/^(---+|\*\*\*+|___+)\s*$/.test(lines[i])
    ) { para.push(lines[i]); i += 1; }
    out.push(
      <p key={key++} style={{ margin: '0 0 14px', color: c.text, fontSize: 14, lineHeight: 1.8 }}>
        {renderInline(para.join(' '), c)}
      </p>,
    );
  }

  return out;
}

// `code`, **bold**, *italic*/_italic_, [text](href)
const INLINE_RE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]]*\]\([^)\s]+\))/g;

function renderInline(text, c) {
  const src = String(text || '');
  const nodes = [];
  let last = 0;
  let m;
  let key = 0;

  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(src)) !== null) {
    if (m.index > last) nodes.push(src.slice(last, m.index));
    const tok = m[0];

    if (tok.startsWith('`')) {
      nodes.push(
        <code key={key++} style={{
          background: c.inlineBg, color: c.inlineText, borderRadius: 4, padding: '1px 5px',
          fontSize: '.9em', fontFamily: 'var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
        }}>{tok.slice(1, -1)}</code>,
      );
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={key++} style={{ fontWeight: 700, color: c.heading }}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*') || tok.startsWith('_')) {
      nodes.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    } else {
      const link = tok.match(/^\[([^\]]*)\]\(([^)\s]+)\)$/);
      const label = link[1] || link[2];
      // Anything not plainly safe (javascript:, data:, …) renders as text.
      if (SAFE_LINK.test(link[2])) {
        nodes.push(
          <a key={key++} href={link[2]} target="_blank" rel="noopener noreferrer"
            style={{ color: c.link, textDecoration: 'none', borderBottom: `1px solid ${c.link}44` }}>{label}</a>,
        );
      } else {
        nodes.push(label);
      }
    }
    last = m.index + tok.length;
  }
  if (last < src.length) nodes.push(src.slice(last));
  return nodes;
}
